/**
 * What the connection did, with the times on it.
 *
 * Frank's rule for this bug was measure, do not guess, and a phone has no
 * console to measure with. Everything worth knowing about a connection that
 * dies happens while the screen is off and the developer is not holding the
 * device, so unless it is written down as it happens there is nothing to look
 * at afterwards but a symptom.
 *
 * So: a small ring of timestamped lines, on both sides, that says when the
 * channel opened, when the page was hidden, when the channel closed and what
 * closed it, when a silent reconnect was tried and whether it worked. It is
 * shown on screen because that is the only place a phone can show it.
 *
 * Nothing here records content. Kinds of event and how long since the last
 * one, which is what a timing question needs and all it needs.
 */

/** Enough to hold a night of sleeping and waking, not enough to grow. */
export const KEPT = 60;

const watchers = new Set();

/**
 * MEETLOG, 11 oktober 2026. De regels staan ook in localStorage, want precies
 * het moment dat ertoe doet - de telefoon gaat op slot en iOS laadt de PWA
 * daarna opnieuw - wiste een log dat alleen in het geheugen stond. Wat er na
 * het opnieuw openen staat, is dan wat er VOOR het slot gebeurde, plus de
 * regel `app-start` die zegt dat de pagina opnieuw is geladen.
 */
export const OPSLAG = "novamund_verbindingslog";

function bewaarde(storage = globalThis.localStorage) {
  try {
    const ruw = storage?.getItem(OPSLAG);
    const gelezen = ruw ? JSON.parse(ruw) : [];
    return Array.isArray(gelezen) ? gelezen.slice(0, KEPT) : [];
  } catch {
    return [];
  }
}

function bewaar(storage = globalThis.localStorage) {
  try {
    storage?.setItem(OPSLAG, JSON.stringify(lines));
  } catch {
    // Vol of geweigerd: het log op het scherm werkt dan nog, alleen niet na herladen.
  }
}

let lines = bewaarde();

function announce() {
  bewaar();
  const snapshot = [...lines];
  watchers.forEach((watcher) => watcher(snapshot));
}

/** Voor de toetsen: lees het log opnieuw uit de opslag, zoals na herladen. */
export function laadConnectionLog(storage = globalThis.localStorage) {
  lines = bewaarde(storage);
  return [...lines];
}

/**
 * Write a line.
 *
 * @param event  short, fixed vocabulary: "channel-open", "channel-closed",
 *               "hidden", "visible", "rejoin-try", "rejoin-ok", "rejoin-failed",
 *               "ping-late", "appointment-kept", "app-start"
 * @param detail one short human phrase, never content
 */
export function noteConnection(event, detail = "", at = Date.now()) {
  const previous = lines[0];
  lines = [{ at, event, detail, since: previous ? at - previous.at : 0 }, ...lines].slice(0, KEPT);
  announce();
  return lines[0];
}

/** Newest first. */
export function connectionLog() {
  return [...lines];
}

export function watchConnectionLog(watcher) {
  watchers.add(watcher);
  watcher([...lines]);
  return () => watchers.delete(watcher);
}

export function clearConnectionLog() {
  lines = [];
  announce();
}

/**
 * PUNT 4d, 11 oktober 2026: de regels in het Nederlands. De OPSLAG houdt de vaste
 * codes, zodat een log van voor vandaag en een log van na vandaag hetzelfde
 * betekenen; alleen wat je leest is vertaald. Een onbekende code blijft staan.
 */
export const GEBEURTENIS = {
  "app-start": "app gestart",
  "channel-open": "verbinding open",
  "channel-closed": "verbinding dicht",
  "ping-late": "ping te laat",
  "hidden": "app op de achtergrond",
  "visible": "app weer in beeld",
  "appointment-kept": "afspraak bewaard",
  "rejoin-try": "opnieuw verbinden",
  "rejoin-ok": "opnieuw verbonden",
  "rejoin-failed": "opnieuw verbinden mislukt",
};

/** One line as a person reads it: the clock time, the event, the gap. */
export function describeLine(line, formatter = null) {
  const clock = formatter
    ? formatter(line.at)
    : new Date(line.at).toLocaleTimeString(undefined, { hour12: false });
  const gap = line.since ? ` (+${Math.round(line.since / 1000)}s)` : "";
  const naam = GEBEURTENIS[line.event] || line.event;
  return `${clock} ${naam}${line.detail ? ` ${line.detail}` : ""}${gap}`;
}
